import Vue from 'vue';

/**
 * Состояние фильтров списка оборудования.
 *
 * Своё, а не параметризованный createRequestFilters из js/requestFilters: оттуда нам нужны
 * от силы половина строк, а scope, чипы панели, дефолты по разделам, псевдостатус «новые»
 * и сборка payload с ключами filterStatusBar/filterScope понятны только RequestController.
 * Приём взят тот же — Vue.observable плюс сохранение в localStorage (см. requestFilters/
 * persist.js), а набор полей свой.
 *
 * Статусы и просрочки живут здесь вместе: в интерфейсе это одна строка пилюль, где
 * просрочки — такие же переключатели, только отвечающие не за значение колонки status,
 * а за отдельный срез выборки.
 */

// v3: city и legalEntity теперь хранят ИДЕНТИФИКАТОРЫ — cities.id и
// inventory_attributes.id, — а не текст значения. Сохранённые под v2 строки
// («Ижевск») новый бэкенд отвергает с 422, а истолковать их как id нельзя:
// совпадение названия с числом ничего не значит. Поэтому ключ меняется, а старое
// состояние просто отбрасывается — при первом открытии фильтры города и юр. лица
// окажутся пустыми, и это дешевле любой попытки миграции.
//
// История ключа: v1 — id города и название подразделения держателя; v2 — текстовые
// значения атрибутов самой техники; v3 — идентификаторы справочников.
const STORAGE_KEY = 'inventoryFilters.items.v3';

/**
 * Ключи просрочек. Совпадают с именами параметров запроса и с countKey счётчиков —
 * так между строкой пилюль, состоянием и API нет ни одной таблицы соответствия.
 */
export const OVERDUE_RETURN = 'overdue_return';
export const OVERDUE_RESERVE = 'overdue_reserve';

const OVERDUE_KEYS = [OVERDUE_RETURN, OVERDUE_RESERVE];

function emptyState() {
    return {
        search: '',
        // Статусы и просрочки вперемешку: числа — значения колонки status,
        // строки OVERDUE_* — срезы выборки.
        statuses: [],
        // Юр. лицо и город — свойства самой единицы техники, а не держателя: у предмета
        // на складе они есть, а держателя нет вовсе.
        //
        // Хранятся идентификаторы, не текст. Город — cities.id, юр. лицо —
        // inventory_attributes.id строки-представителя: бэкенд сам разворачивает его
        // в группу задвоенных написаний справочника.
        legalEntity: [],
        city: [],
        holder: [],
        attributes: {},
    };
}

function load() {
    try {
        const saved = JSON.parse(localStorage.getItem(STORAGE_KEY));

        return saved && typeof saved === 'object' ? saved : {};
    } catch (error) {
        // Квота, приватный режим, испорченный JSON — сохранение фильтров не критично.
        return {};
    }
}

export function createInventoryFilters() {
    const state = Vue.observable({ ...emptyState(), ...load() });

    // Поиск намеренно не сохраняется: возвращаться в раздел с непонятно откуда взявшимся
    // запросом в строке — худшее, что фильтры могут сделать с пользователем.
    function persist() {
        try {
            localStorage.setItem(
                STORAGE_KEY,
                JSON.stringify({
                    statuses: state.statuses,
                    legalEntity: state.legalEntity,
                    city: state.city,
                    holder: state.holder,
                    attributes: state.attributes,
                }),
            );
        } catch (error) {
            // см. load()
        }
    }

    function setSearch(search) {
        state.search = search || '';
    }

    function toggleStatus(status) {
        const next = [...state.statuses];
        const index = next.indexOf(status);

        if (index === -1) {
            next.push(status);
        } else {
            next.splice(index, 1);
        }

        state.statuses = next;
        persist();
    }

    function clearStatuses() {
        state.statuses = [];
        persist();
    }

    function setValues(name, values) {
        state[name] = Array.isArray(values) ? values : [];
        persist();
    }

    function setAttributeValues(type, values) {
        const next = { ...state.attributes };

        if (Array.isArray(values) && values.length > 0) {
            next[type] = values;
        } else {
            delete next[type];
        }

        state.attributes = next;
        persist();
    }

    function resetAll() {
        const empty = emptyState();

        Object.keys(empty).forEach((key) => {
            state[key] = empty[key];
        });

        persist();
    }

    /**
     * Есть ли что сбрасывать. Учитывает всё состояние целиком, включая статусы
     * и просрочки: раньше кнопка очистки их не видела и не показывалась, хотя сама
     * очистка их сбрасывала.
     */
    function isDirty() {
        return Boolean(
            state.search ||
                state.statuses.length ||
                state.legalEntity.length ||
                state.city.length ||
                state.holder.length ||
                Object.keys(state.attributes).length,
        );
    }

    /**
     * Тело запроса к /inventory/items/query, /stats и /export.
     *
     * Пустые фильтры не отправляются вовсе: на бэкенде отсутствие параметра и есть
     * «фильтр не применяется», и слать пустые массивы ради симметрии значит гонять лишнее.
     */
    function buildQuery() {
        const query = {};

        if (state.search) {
            query.search = state.search;
        }

        const statuses = state.statuses.filter((status) => OVERDUE_KEYS.indexOf(status) === -1);

        if (statuses.length > 0) {
            query.status = statuses;
        }

        OVERDUE_KEYS.forEach((key) => {
            if (state.statuses.indexOf(key) !== -1) {
                query[key] = true;
            }
        });

        if (state.legalEntity.length > 0) {
            query.legal_entity = state.legalEntity;
        }

        if (state.city.length > 0) {
            query.city = state.city;
        }

        if (state.holder.length > 0) {
            query.holder = state.holder;
        }

        if (Object.keys(state.attributes).length > 0) {
            query.filters = state.attributes;
        }

        return query;
    }

    return {
        state,
        setSearch,
        toggleStatus,
        clearStatuses,
        setValues,
        setAttributeValues,
        resetAll,
        isDirty,
        buildQuery,
    };
}
